import { useMemo } from 'react';
import { useUnit } from 'effector-react';
import clsx from 'clsx';
import { $profileFeatureStore } from '@lib/store';
import { useAccountFlow, useCurrencySymbol } from '@shared/hooks';
import { AmountUtils, FormatUtils, PaletteUtils } from '@shared/utils';
import type { AccountResponse } from '@shared/api/rest/accounts';
import { ColorsEnum, Icon, Text } from '@shared/widgets';
import CardIcon from '@assets/icons/card.svg';
import styles from '../../styles/accountCardTile.module.scss';

interface Props {
  account: AccountResponse;
}

export const AccountCardSmallTile = (props: Props) => {
  const profileFeatureStore = useUnit($profileFeatureStore.store);

  const { accountFlow } = useAccountFlow(profileFeatureStore.data?.accountFlows, props.account.id);
  const symbol = useCurrencySymbol(props.account.currency);

  const progress = useMemo(() => {
    if (props.account.balance) {
      return props.account.balance / (props.account.creditLimit / 100);
    } else return 0;
  }, [props.account]);

  if (accountFlow?.view === false) return null;
  return (
    <div className='flex flex-col bg-white dark:bg-soft-black'>
      <div className='flex justify-between'>
        <div className='flex flex-col mt-5 ml-5'>
          <Text size='3xl'>{AmountUtils.transformToAmountWithCurrency(props.account.balance, props.account.currency)}</Text>
          <Text className='mt-2' weight='medium' size='xs' color={ColorsEnum.Secondary}>
            {props.account.name}
          </Text>
          <Text className='mt-1' weight='medium' size='xs' color={ColorsEnum.Secondary}>
            {props.account.productName}
          </Text>
          <Text className='mt-1' weight='medium'>
            {props.account.name}
          </Text>
        </div>
        <div
          className={clsx(
            'w-36 h-32 pt-4 pr-1 pb-2 pl-9 rounded-tr-sm flex-none flex flex-col items-center justify-end overflow-hidden text-center relative before:bg-white before:dark:bg-soft-black',
            PaletteUtils.isInsigniaPalette && 'bg-soft-black dark:bg-slate before:border-r-primary',
            PaletteUtils.isVcfcPalette && 'bg-primary dark:before:border-r-ice',
            styles['account__top-right'],
          )}
        >
          <div
            className={clsx(
              'mb-1 before:content-[attr(data-icon)] after:content-[attr(data-icon)] ',
              PaletteUtils.isInsigniaPalette && 'before:text-primary after:text-primary',
              PaletteUtils.isVcfcPalette && 'before:text-white after:text-white',
              styles['account__icon'],
            )}
            data-icon={symbol}
          />
          <Text weight='semibold' size='sm' color={ColorsEnum.White}>
            {props.account.status}
          </Text>
        </div>
      </div>
      <div className='flex flex-col px-5 py-4'>
        <div className='relative w-full h-1 rounded bg-light'>
          <div className='absolute h-full left-0 rounded bg-primary' style={{ width: `${progress}%` }}></div>
        </div>
        <div className='flex justify-between mt-2'>
          <Text size='sm' color={ColorsEnum.Secondary}>
            Spending limit
          </Text>
          <Text weight='medium' size='xs'>
            {`${AmountUtils.transformToAmountWithCurrency(props.account.balance, props.account.currency)} / ${AmountUtils.transformToAmountWithCurrency(props.account.creditLimit, props.account.currency)}`}
          </Text>
        </div>
        <div className='w-full h-[1px] bg-gray-black my-3'></div>
        <div className='flex justify-between'>
          <Text size='sm' color={ColorsEnum.Secondary}>
            Account number
          </Text>
          <Text weight='medium' size='xs'>
            {FormatUtils.toSliceNumber(props.account.accountNumber)}
          </Text>
        </div>
        <div className='flex justify-between mt-2'>
          <Text size='sm' color={ColorsEnum.Secondary}>
            Cards
          </Text>
          <div className='flex gap-1'>
            <Text weight='medium' size='xs'>
              {props.account.cardsCount}
            </Text>
            <Icon icon={<CardIcon width='16' height='16' />} />
          </div>
        </div>
      </div>
    </div>
  );
};
